import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import updateParams from '../../../../../utils/updateParams';
import SharedButton from '../../../../../global/Button/Button';
import { SourceButtonGroupProps } from '../../../../../types';
import SharedDialog from '../../../../../global/Modal/Modal';

const specialFilters = ['CURATED', 'CLINICALTRIALS', 'INFERRED', 'MODELS', 'TEXTMINING_HUMAN', 'ALL'];

function getCurrentSource(search: string): string | null {
  const params = new URLSearchParams(search);
  return params.get('source');
}

export default function SourceButtonGroup({
  sourceList,
  category,
  isScoreSource = true,
  selectedSourceWeights = [],
}: SourceButtonGroupProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentSource = getCurrentSource(location.search);

  // Check if this source is in the selected weights
  const isSourceSelected = (sourceName: string) => {
    return selectedSourceWeights.some(sw => sw.name === sourceName);
  };

  const primarySources = sourceList.filter(value => value === category || specialFilters.includes(value));

  const [sliderValues, setSliderValues] = useState<Record<string, number>>({
    CURATED: 0.7,
    CLINICALTRIALS: 0.1,
    INFERRED: 0.25,
    MODELS: 0.1,
    TEXTMINING_HUMAN: 0.4,
    ALL: 1,
  });

  const [showClearWeightsModal, setShowClearWeightsModal] = useState(false);
  const [pendingSource, setPendingSource] = useState<string | null>(null);

  const handleClick = (value: string) => {
    const storedWeights = sessionStorage.getItem('sourceWeights');
    const hasCustomWeights = storedWeights && JSON.parse(storedWeights).length > 0;

    const isPartOfWeights = selectedSourceWeights.some(sw => sw.name === value);

    if (hasCustomWeights && !isPartOfWeights && selectedSourceWeights.length > 0) {
      setPendingSource(value);
      setShowClearWeightsModal(true);
      return;
    }

    proceedWithSourceChange(value);
  };

  const proceedWithSourceChange = (value: string) => {
    sessionStorage.removeItem('sourceWeights');

    const queryString = updateParams({ source: value });
    const newUrl = `${location.pathname}?${queryString}`;
    window.location.href = newUrl;
  };

  const handleConfirmClearWeights = () => {
    if (pendingSource) {
      proceedWithSourceChange(pendingSource);
    }
    setShowClearWeightsModal(false);
    setPendingSource(null);
  };

  const handleCancelClearWeights = () => {
    setShowClearWeightsModal(false);
    setPendingSource(null);
  };

  const handleSliderChange = (source: string, value: number) => {
    setSliderValues(prev => ({ ...prev, [source]: value }));
  };

  const filteredSourceList = !isScoreSource
    ? sourceList
    : sourceList.filter(value => value === category || specialFilters.includes(value));

  return (
    <>
      {/* Confirmation Modal */}
      <SharedDialog
        isOpen={showClearWeightsModal}
        onClose={handleCancelClearWeights}
        title="Clear Custom Weights?"
        description="You have custom source weights applied. Selecting a different source will clear these weights."
        confirmText="Yes, Clear Weights"
        cancelText="Cancel"
        hasConfirmBtn
        onConfirm={handleConfirmClearWeights}
      >
        <div className="mt-3">
          <p className="text-sm text-gray-600">
            Current custom weights will be removed and the source will be changed to{' '}
            <strong>{pendingSource?.replace('_', ' ')}</strong>.
          </p>
        </div>
      </SharedDialog>

      <div className="flex flex-wrap gap-4" id="SUBMENU">
        {filteredSourceList.map(value => {
          const isCategoryMain = category && value === category;
          const sliderValue = sliderValues[value] ?? 0;
          const isSelected = isSourceSelected(value);
          return (
            <div key={value} className="flex items-center gap-3">
              <SharedButton
                onClick={() => handleClick(value)}
                isActive={currentSource === value || (selectedSourceWeights.length > 1 && isSelected)}
                size="sm"
                className="text-blue"
                variant={isCategoryMain || specialFilters.includes(value) ? 'primary' : 'secondary'}
              >
                {value.replace('_', ' ')}
              </SharedButton>

              {isScoreSource && primarySources && (
                <div className="flex items-center">
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={sliderValue}
                    onChange={e => handleSliderChange(value, parseFloat(e.target.value))}
                    className="w-28 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer score-slider"
                  />
                  <span className="text-xs text-gray-700 ml-2">{sliderValue.toFixed(2)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
